/**
 * Every problem the employee import found, grouped by spreadsheet row.
 * Each row says whether it will be imported, and each problem explains what
 * is wrong, which cell, what it clashes with, and how to fix it. The most
 * common problems are summarised on top (click one to filter), and the whole
 * list can be downloaded as a report to fix the file.
 * Shared by ImportPreviewModal (before commit) and ImportResultsModal (after).
 */
import React, { useMemo, useState } from "react";
import { XCircle, AlertTriangle, Lightbulb, Search, Download, Ban, CheckCircle2 } from "lucide-react";
import { buildRowIssues, downloadIssuesReport, fieldLabel } from "../../utils/importIssues";

const RED = "#ef4444";
const AMBER = "#f59e0b";
const PAGE = 60;

const ImportIssuesPanel = ({ failed, parseWarnings, isRtl, isDark, fonts, committed = false, fileName }) => {
  const [filter, setFilter] = useState("all"); // all | blocked | warning | <issue code>
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE);

  const rows = useMemo(() => buildRowIssues({ failed, parseWarnings }, isRtl), [failed, parseWarnings, isRtl]);

  const blockedCount = rows.filter((r) => r.blocked).length;
  const warningCount = rows.length - blockedCount;

  // Most frequent problem types, for the summary strip.
  const topIssues = useMemo(() => {
    const counts = new Map();
    for (const r of rows) {
      for (const code of new Set(r.issues.map((i) => i.code))) {
        const issue = r.issues.find((i) => i.code === code);
        const prev = counts.get(code) || { code, title: issue.title, severity: issue.severity, rows: 0 };
        prev.rows += 1;
        counts.set(code, prev);
      }
    }
    return [...counts.values()].sort((a, b) => b.rows - a.rows).slice(0, 6);
  }, [rows]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "blocked" && !r.blocked) return false;
      if (filter === "warning" && r.blocked) return false;
      if (!["all", "blocked", "warning"].includes(filter) && !r.issues.some((i) => i.code === filter)) return false;
      if (!needle) return true;
      return (
        String(r.row) === needle ||
        (r.employee?.name || "").toLowerCase().includes(needle) ||
        String(r.employee?.clientCode || "").toLowerCase().includes(needle)
      );
    });
  }, [rows, filter, query]);

  if (!rows.length) return null;

  const t = (ar, en) => (isRtl ? ar : en);
  const c = {
    card: isDark ? "rgba(15,23,42,0.55)" : "#ffffff",
    soft: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
    border: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)",
    text: isDark ? "#f1f5f9" : "#0f172a",
    sub: isDark ? "#94a3b8" : "#64748b",
  };
  const blockedLabel = committed ? t("لم يُستورد", "Not imported") : t("لن يُستورد", "Won't be imported");
  const warnLabel = committed ? t("استُورد بملاحظات", "Imported with notes") : t("سيُستورد بملاحظات", "Imports with notes");

  const segment = (key, label, count, color) => {
    const active = filter === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => { setFilter(key); setVisible(PAGE); }}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999,
          border: `1.5px solid ${active ? color : c.border}`, background: active ? `${color}14` : "transparent",
          color: active ? color : c.sub, fontSize: fonts.tiny, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
        }}
      >
        {label}
        <span style={{ minWidth: 20, padding: "0 6px", borderRadius: 999, background: active ? color : c.soft, color: active ? "#fff" : c.sub, fontSize: "0.68rem", lineHeight: "18px", textAlign: "center" }}>{count}</span>
      </button>
    );
  };

  return (
    <section style={{ border: `1px solid ${c.border}`, borderRadius: 16, background: c.card, overflow: "hidden" }}>
      {/* ── Summary ── */}
      <div style={{ padding: "16px 18px 14px", borderBottom: `1px solid ${c.border}`, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: fonts.body, fontWeight: 800, color: c.text }}>
              {t(`مشاكل في ${rows.length} صف`, `Problems in ${rows.length} row${rows.length > 1 ? "s" : ""}`)}
            </div>
            <div style={{ fontSize: fonts.tiny, color: c.sub, marginTop: 2 }}>
              {t(
                "كل صف موضح فيه سبب المشكلة والخانة المقصودة وطريقة الحل.",
                "Each row explains what's wrong, which cell, and how to fix it.",
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => downloadIssuesReport(rows, isRtl, `${(fileName || "employees").replace(/\.[^.]+$/, "")}-issues.csv`)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10,
              border: `1.5px solid ${c.border}`, background: c.soft, color: c.text,
              fontSize: fonts.tiny, fontWeight: 700, cursor: "pointer",
            }}
          >
            <Download size={14} />
            {t("تحميل تقرير المشاكل", "Download issues report")}
          </button>
        </div>

        {/* Most common problems, click to filter */}
        {topIssues.length > 1 && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {topIssues.map((it) => {
              const color = it.severity === "error" ? RED : AMBER;
              const active = filter === it.code;
              return (
                <button
                  key={it.code}
                  type="button"
                  onClick={() => { setFilter(active ? "all" : it.code); setVisible(PAGE); }}
                  title={t("اضغط لعرض الصفوف التي بها هذه المشكلة فقط", "Click to show only rows with this problem")}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8, paddingBlock: 5,
                    paddingInlineStart: 6, paddingInlineEnd: 10, borderRadius: 10, cursor: "pointer",
                    border: `1px solid ${active ? color : `${color}33`}`, background: active ? `${color}1f` : `${color}0d`,
                    color: c.text, fontSize: fonts.tiny, fontWeight: 600,
                  }}
                >
                  <span style={{ minWidth: 24, height: 20, padding: "0 6px", borderRadius: 6, background: color, color: "#fff", fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem" }}>
                    {it.rows}
                  </span>
                  {it.title}
                </button>
              );
            })}
          </div>
        )}

        {/* Filters + search */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {segment("all", t("الكل", "All"), rows.length, "#3b82f6")}
          {blockedCount > 0 && segment("blocked", blockedLabel, blockedCount, RED)}
          {warningCount > 0 && segment("warning", warnLabel, warningCount, AMBER)}
          <div style={{ marginInlineStart: "auto", position: "relative", minWidth: 200, flex: "0 1 260px" }}>
            <Search size={14} color={c.sub} style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", insetInlineStart: 10 }} />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setVisible(PAGE); }}
              placeholder={t("ابحث برقم الصف أو الاسم أو الكود", "Search row, name or code")}
              style={{
                width: "100%", boxSizing: "border-box", padding: "7px 10px", paddingInlineStart: 30, borderRadius: 10,
                border: `1px solid ${c.border}`, background: c.soft, color: c.text, fontSize: fonts.tiny, outline: "none",
              }}
            />
          </div>
        </div>
      </div>

      {/* ── Rows ── */}
      <div style={{ maxHeight: 460, overflowY: "auto", scrollbarWidth: "thin" }}>
        {shown.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: c.sub, fontSize: fonts.small }}>
            {t("لا توجد صفوف مطابقة.", "No matching rows.")}
          </div>
        )}
        {shown.slice(0, visible).map((r) => {
          const color = r.blocked ? RED : AMBER;
          return (
            <div key={r.row} style={{ display: "flex", borderBottom: `1px solid ${c.border}` }}>
              {/* Row number gutter */}
              <div style={{
                width: 64, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
                paddingTop: 16, gap: 2, background: `${color}0d`, borderInlineEnd: `3px solid ${color}`,
              }}>
                <span style={{ fontSize: "0.62rem", fontWeight: 700, color: c.sub, textTransform: "uppercase" }}>{t("صف", "Row")}</span>
                <span style={{ fontSize: "1.15rem", fontWeight: 800, color, lineHeight: 1 }}>{r.row}</span>
              </div>

              <div style={{ flex: 1, minWidth: 0, padding: "12px 16px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                  <span style={{ fontSize: fonts.small, fontWeight: 700, color: c.text }}>
                    <bdi>{r.employee?.name || t("بدون اسم", "No name")}</bdi>
                  </span>
                  {r.employee?.clientCode && (
                    <span style={{ fontSize: "0.7rem", fontWeight: 700, color: c.sub, background: c.soft, border: `1px solid ${c.border}`, borderRadius: 6, padding: "1px 7px" }}>
                      {t("كود", "Code")} <bdi>{r.employee.clientCode}</bdi>
                    </span>
                  )}
                  <span style={{
                    marginInlineStart: "auto", display: "inline-flex", alignItems: "center", gap: 5,
                    fontSize: "0.7rem", fontWeight: 700, color, background: `${color}14`, borderRadius: 999, padding: "2px 10px",
                  }}>
                    {r.blocked ? <Ban size={11} /> : <CheckCircle2 size={11} />}
                    {r.blocked ? blockedLabel : warnLabel}
                  </span>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {r.issues.map((issue, idx) => {
                    const ic = issue.severity === "error" ? RED : AMBER;
                    const Icon = issue.severity === "error" ? XCircle : AlertTriangle;
                    return (
                      <div key={idx} style={{ display: "flex", gap: 10 }}>
                        <Icon size={16} color={ic} style={{ flexShrink: 0, marginTop: 2 }} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontSize: fonts.small, fontWeight: 700, color: c.text }}>{issue.title}</span>
                            {issue.field && (
                              <span style={{ fontSize: "0.68rem", fontWeight: 600, color: ic, border: `1px solid ${ic}40`, borderRadius: 6, padding: "0 6px" }}>
                                {fieldLabel(issue.field, isRtl)}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: fonts.tiny, color: c.sub, lineHeight: 1.7, marginTop: 2, overflowWrap: "anywhere" }}>
                            {issue.message}
                          </div>
                          {issue.fix && (
                            <div style={{ display: "flex", gap: 6, alignItems: "flex-start", marginTop: 6, padding: "6px 10px", borderRadius: 8, background: c.soft, fontSize: fonts.tiny, color: c.text, lineHeight: 1.6 }}>
                              <Lightbulb size={13} color="#10b981" style={{ flexShrink: 0, marginTop: 3 }} />
                              <span>{issue.fix}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
        {shown.length > visible && (
          <div style={{ padding: 12, textAlign: "center" }}>
            <button
              type="button"
              onClick={() => setVisible((v) => v + PAGE)}
              style={{ padding: "7px 16px", borderRadius: 999, border: `1px solid ${c.border}`, background: c.soft, color: c.text, fontSize: fonts.tiny, fontWeight: 700, cursor: "pointer" }}
            >
              {t(`عرض المزيد (${shown.length - visible})`, `Show more (${shown.length - visible})`)}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ImportIssuesPanel;
